import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, Marker, Popup, Circle, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';

const ISRAEL_CENTER = { lat: 31.5, lng: 34.9 };

type Point = { lat: number; lng: number };

type RidePin = {
  id: string;
  driver_name: string;
  origin: string;
  origin_lat: number | null;
  origin_lng: number | null;
  seats_total: number;
  seats_taken: number;
};

type RequestPin = {
  id: string;
  passenger_name: string;
  origin: string;
  origin_lat: number | null;
  origin_lng: number | null;
  status: string;
  ride_id: string | null;
  seats_needed: number;
  rides?: { driver_name: string } | null;
};

type Selection =
  | { kind: 'venue' }
  | { kind: 'ride'; id: string }
  | { kind: 'request'; id: string }
  | null;

function pinIcon(url: string, w: number, h: number) {
  return L.icon({
    iconUrl: url,
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -h + 6],
    className: 'wr-pin',
  });
}

function FitToPoints({ points }: { points: Point[] }) {
  const map = useMap();
  const fp = points.map((p) => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`).join('|');
  const fitted = useRef('');

  useEffect(() => {
    if (fp === fitted.current) return;
    fitted.current = fp;
    if (points.length === 0) {
      map.setView([ISRAEL_CENTER.lat, ISRAEL_CENTER.lng], 8);
      return;
    }
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 12);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 13, animate: false });
  }, [fp, map, points]);

  return null;
}

export default function OsmRidesMap({
  icons,
  venuePoint,
  venueName,
  venueAddress,
  rides,
  requests,
  showDrivers,
  showPending,
  showMatched,
  selection,
  selectedPickupIds,
  selectedRidePickups,
  onSelect,
}: {
  icons: { venue: string; ride: string; rideFull: string; request: string; matched: string; pickup: string };
  venuePoint: Point | null;
  venueName: string;
  venueAddress: string | null;
  rides: RidePin[];
  requests: RequestPin[];
  showDrivers: boolean;
  showPending: boolean;
  showMatched: boolean;
  selection: Selection;
  selectedPickupIds: Set<string>;
  selectedRidePickups: RequestPin[];
  onSelect: (s: Selection) => void;
}) {
  const leafletIcons = useMemo(
    () => ({
      venue: pinIcon(icons.venue, 40, 52),
      ride: pinIcon(icons.ride, 36, 46),
      rideSel: pinIcon(icons.ride, 44, 56),
      rideFull: pinIcon(icons.rideFull, 36, 46),
      request: pinIcon(icons.request, 32, 42),
      matched: pinIcon(icons.matched, 32, 42),
      pickup: pinIcon(icons.pickup, 44, 56),
    }),
    [icons],
  );

  const fitPoints = useMemo(() => {
    const pts: Point[] = [];
    if (venuePoint) pts.push(venuePoint);
    if (showDrivers) {
      for (const r of rides) {
        if (r.origin_lat != null && r.origin_lng != null) {
          pts.push({ lat: Number(r.origin_lat), lng: Number(r.origin_lng) });
        }
      }
    }
    for (const q of requests) {
      if (q.origin_lat == null || q.origin_lng == null) continue;
      if (q.status === 'pending' && showPending) {
        pts.push({ lat: Number(q.origin_lat), lng: Number(q.origin_lng) });
      }
      if (q.status === 'matched' && (showMatched || selectedPickupIds.has(q.id))) {
        pts.push({ lat: Number(q.origin_lat), lng: Number(q.origin_lng) });
      }
    }
    return pts;
  }, [venuePoint, rides, requests, showDrivers, showPending, showMatched, selectedPickupIds]);

  const center = venuePoint ?? ISRAEL_CENTER;

  return (
    <MapContainer
      center={[center.lat, center.lng]}
      zoom={venuePoint ? 10 : 8}
      className="rides-osm-map"
      zoomControl
      attributionControl
      minZoom={7}
      maxZoom={16}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitToPoints points={fitPoints} />

      {venuePoint && (
        <Marker
          position={[venuePoint.lat, venuePoint.lng]}
          icon={leafletIcons.venue}
          zIndexOffset={1000}
          eventHandlers={{ click: () => onSelect({ kind: 'venue' }) }}
        >
          {selection?.kind === 'venue' && (
            <Popup autoPan eventHandlers={{ remove: () => onSelect(null) }}>
              <div dir="rtl" className="min-w-[180px] font-[Heebo,sans-serif]">
                <div className="font-bold text-[#3A3340] text-[15px]">{venueName}</div>
                {venueAddress && <div className="text-[12px] text-[#5C6578] mt-1">{venueAddress}</div>}
              </div>
            </Popup>
          )}
        </Marker>
      )}

      {showDrivers &&
        rides.map((r) => {
          if (r.origin_lat == null || r.origin_lng == null) return null;
          const isSel = selection?.kind === 'ride' && selection.id === r.id;
          const full = r.seats_taken >= r.seats_total;
          return (
            <Marker
              key={r.id}
              position={[Number(r.origin_lat), Number(r.origin_lng)]}
              icon={isSel ? leafletIcons.rideSel : full ? leafletIcons.rideFull : leafletIcons.ride}
              opacity={selection?.kind === 'ride' && !isSel ? 0.4 : 1}
              zIndexOffset={isSel ? 900 : 200}
              eventHandlers={{ click: () => onSelect({ kind: 'ride', id: r.id }) }}
            >
              {isSel && (
                <Popup autoPan eventHandlers={{ remove: () => onSelect(null) }}>
                  <div dir="rtl" className="min-w-[200px] font-[Heebo,sans-serif]">
                    <div className="text-[11px] font-semibold text-[#2A6B58] uppercase mb-1">נהג</div>
                    <div className="font-bold text-[#3A3340] text-[15px]">{r.driver_name}</div>
                    <div className="text-[12px] text-[#5C6578] mt-1">
                      {r.origin} · מקומות {r.seats_taken}/{r.seats_total}
                    </div>
                    {selectedRidePickups.length > 0 ? (
                      <div className="mt-2 border-t border-[#E8EDF3] pt-2 space-y-1">
                        <div className="text-[11px] font-semibold text-[#C45C26]">
                          אוסף {selectedRidePickups.length} נוסעים
                        </div>
                        {selectedRidePickups.map((p) => (
                          <div key={p.id} className="text-[12px] text-[#3A3340]">
                            <span className="font-semibold">{p.passenger_name}</span>
                            <span className="text-[#5C6578]"> · {p.origin}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] text-[#8B93A7]">אין נוסעים משובצים עדיין</div>
                    )}
                  </div>
                </Popup>
              )}
            </Marker>
          );
        })}

      {requests.map((q) => {
        if (q.origin_lat == null || q.origin_lng == null) return null;
        const isPickupOfSelected = selectedPickupIds.has(q.id);
        if (q.status === 'pending' && !showPending) return null;
        if (q.status === 'matched' && !showMatched && !isPickupOfSelected) return null;
        if (q.status !== 'pending' && q.status !== 'matched') return null;
        const isSel = selection?.kind === 'request' && selection.id === q.id;
        return (
          <Marker
            key={q.id}
            position={[Number(q.origin_lat), Number(q.origin_lng)]}
            icon={
              isPickupOfSelected
                ? leafletIcons.pickup
                : q.status === 'matched'
                  ? leafletIcons.matched
                  : leafletIcons.request
            }
            opacity={selection?.kind === 'ride' && !isPickupOfSelected && q.status !== 'pending' ? 0.3 : 1}
            zIndexOffset={isPickupOfSelected ? 850 : 100}
            eventHandlers={{ click: () => onSelect({ kind: 'request', id: q.id }) }}
          >
            {isSel && (
              <Popup autoPan eventHandlers={{ remove: () => onSelect(null) }}>
                <div dir="rtl" className="min-w-[180px] font-[Heebo,sans-serif]">
                  <div className="font-bold text-[#3A3340] text-[15px]">{q.passenger_name}</div>
                  <div className="text-[12px] text-[#5C6578] mt-1">{q.origin}</div>
                  {q.status === 'matched' && q.rides && (
                    <div className="mt-1 text-[12px] text-[#944E68] font-semibold">
                      שובץ ל-{q.rides.driver_name}
                    </div>
                  )}
                </div>
              </Popup>
            )}
          </Marker>
        );
      })}

      {selectedRidePickups.map((q) =>
        q.origin_lat != null && q.origin_lng != null ? (
          <Circle
            key={`ring-${q.id}`}
            center={[Number(q.origin_lat), Number(q.origin_lng)]}
            radius={450}
            pathOptions={{
              color: '#C45C26',
              opacity: 0.95,
              weight: 2,
              fillColor: '#C45C26',
              fillOpacity: 0.18,
            }}
          />
        ) : null,
      )}
    </MapContainer>
  );
}
